import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API_ORIGIN = process.env.REACT_APP_API_URL || 'https://trading.omniscalping.com';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
  };
}

function MailAddressInput({ label, hint, localValue, onLocalChange, domain, placeholder, id }) {
  return (
    <label className="mail-field mail-address-field" htmlFor={id}>
      {label && <span className="mail-field-label">{label}</span>}
      <div className="mail-address-combo">
        <input
          id={id}
          type="text"
          className="mail-address-local"
          placeholder={placeholder || 'name'}
          value={localValue}
          onChange={(e) => onLocalChange(e.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        <span className="mail-address-at">@</span>
        <span className="mail-address-domain">{domain}</span>
      </div>
      {hint && <small className="mail-field-hint">{hint}</small>}
    </label>
  );
}

function AliasPopover({ aliases, onDelete, busy, onClose }) {
  if (!aliases.length) return null;
  return (
    <div className="mail-alias-popover">
      <div className="mail-alias-popover-head">
        <span>Forwards to this mailbox</span>
        <button type="button" className="mail-alias-popover-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <ul className="mail-alias-popover-list">
        {aliases.map((row) => (
          <li key={row.alias}>
            <code>{row.alias}</code>
            <button
              type="button"
              className="mail-btn mail-btn-xs mail-btn-danger"
              onClick={() => onDelete(row.alias, row.target)}
              disabled={busy}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AdminEmail() {
  const [config, setConfig] = useState(null);
  const [limits, setLimits] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [openAliasFor, setOpenAliasFor] = useState(null);
  const [showAddAlias, setShowAddAlias] = useState(false);
  const [limitsForm, setLimitsForm] = useState({ mailboxQuota: '25GB', messageSize: '35MB' });
  const [showLimitsEdit, setShowLimitsEdit] = useState(false);

  const [addForm, setAddForm] = useState({
    localPart: '',
    password: '',
    generatePassword: true,
    displayName: '',
    omniUserId: '',
    notifyEmail: '',
    emailCredentials: true,
    notes: '',
  });

  const [aliasForm, setAliasForm] = useState({ aliasLocal: '', target: '' });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetGenerate, setResetGenerate] = useState(true);
  const [resetNotifyEmail, setResetNotifyEmail] = useState('');
  const [resetEmailCredentials, setResetEmailCredentials] = useState(true);
  const [editTarget, setEditTarget] = useState(null);

  const pickUserEmail = (userId) => {
    const u = users.find((row) => String(row.id) === String(userId));
    return u?.email || '';
  };

  const load = useCallback(async () => {
    const [accRes, aliasRes, userRes] = await Promise.all([
      fetch(`${API_ORIGIN}/api/admin/mail/accounts`, { headers: authHeaders() }),
      fetch(`${API_ORIGIN}/api/admin/mail/aliases`, { headers: authHeaders() }),
      fetch(`${API_ORIGIN}/api/admin/mail/users`, { headers: authHeaders() }),
    ]);

    if (!accRes.ok) {
      const err = await accRes.json().catch(() => ({}));
      throw new Error(err.error || 'Failed to load mailboxes (admin only)');
    }

    const accJson = await accRes.json();
    const aliasJson = aliasRes.ok ? await aliasRes.json().catch(() => ({ aliases: [] })) : { aliases: [] };
    const userJson = userRes.ok ? await userRes.json() : { users: [] };

    setConfig(accJson.config || null);
    setAccounts(accJson.accounts || []);
    setAliases(aliasJson.aliases || []);
    setUsers(userJson.users || []);
    if (accJson.limits) {
      setLimits(accJson.limits);
      setLimitsForm({
        mailboxQuota: accJson.limits.mailboxQuotaLabel || '25GB',
        messageSize: accJson.limits.messageSizeLabel || '35MB',
      });
    }
  }, []);

  useEffect(() => {
    load().catch((e) => setMsg({ err: true, text: e.message }));
  }, [load]);

  const domain = config?.domain || 'omniscalping.com';
  const host = config?.host || `mail.${domain}`;

  const aliasesByTarget = useMemo(() => {
    const map = {};
    for (const row of aliases) {
      const t = (row.target || '').toLowerCase();
      if (!t) continue;
      if (!map[t]) map[t] = [];
      map[t].push(row);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => a.alias.localeCompare(b.alias));
    }
    return map;
  }, [aliases]);

  const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => {
      const aliasHits = (aliasesByTarget[a.email.toLowerCase()] || []).some((row) => row.alias.includes(q));
      return (
        a.email.includes(q) ||
        (a.displayName || '').toLowerCase().includes(q) ||
        (a.omniEmail || '').toLowerCase().includes(q) ||
        (a.notes || '').toLowerCase().includes(q) ||
        aliasHits
      );
    });
  }, [accounts, search, aliasesByTarget]);

  const createMailbox = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const local = addForm.localPart.trim().toLowerCase();
      const email = local ? `${local}@${domain}` : '';

      if (!email) {
        throw new Error('Enter a mailbox address');
      }

      const res = await fetch(`${API_ORIGIN}/api/admin/mail/accounts`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          email,
          password: addForm.generatePassword ? undefined : addForm.password,
          generatePassword: addForm.generatePassword,
          displayName: addForm.displayName || undefined,
          omniUserId: addForm.omniUserId ? Number(addForm.omniUserId) : undefined,
          notifyEmail: addForm.notifyEmail.trim() || undefined,
          emailCredentials: addForm.emailCredentials,
          notes: addForm.notes || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create mailbox');

      setMsg({ err: false, text: data.message || `Mailbox ${email} created.` });
      setAddForm({
        localPart: '',
        password: '',
        generatePassword: true,
        displayName: '',
        omniUserId: '',
        notifyEmail: '',
        emailCredentials: true,
        notes: '',
      });
      await load();
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const resetMailboxPassword = async (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(
        `${API_ORIGIN}/api/admin/mail/accounts/${encodeURIComponent(resetTarget.email)}/password`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({
            password: resetGenerate ? undefined : resetPassword,
            generatePassword: resetGenerate,
            notifyEmail: resetNotifyEmail.trim() || undefined,
            emailCredentials: resetEmailCredentials,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reset password');

      setMsg({ err: false, text: data.message || 'Password updated.' });
      setResetTarget(null);
      setResetPassword('');
      setResetNotifyEmail('');
      await load();
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const saveMeta = async (e) => {
    e.preventDefault();
    if (!editTarget) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(
        `${API_ORIGIN}/api/admin/mail/accounts/${encodeURIComponent(editTarget.email)}`,
        {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({
            displayName: editTarget.displayName,
            omniUserId: editTarget.omniUserId === '' ? null : Number(editTarget.omniUserId),
            notes: editTarget.notes,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save details');
      setMsg({ err: false, text: data.message });
      setEditTarget(null);
      await load();
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const deleteMailbox = async (email) => {
    if (!window.confirm(`Delete mailbox ${email}? This cannot be undone.`)) return;
    setBusy(true);
    setMsg(null);
    if (openAliasFor === email) setOpenAliasFor(null);
    try {
      const res = await fetch(
        `${API_ORIGIN}/api/admin/mail/accounts/${encodeURIComponent(email)}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to delete mailbox (${res.status})`);
      setMsg({ err: false, text: data.message });
      await load();
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const createAlias = async (e) => {
    e.preventDefault();
    const aliasLocal = aliasForm.aliasLocal.trim().toLowerCase();
    const aliasEmail = aliasLocal.includes('@') ? aliasLocal : (aliasLocal ? `${aliasLocal}@${domain}` : '');

    if (!aliasEmail) {
      setMsg({ err: true, text: 'Enter an alias address (e.g. sales)' });
      return;
    }
    if (!aliasForm.target) {
      setMsg({ err: true, text: 'Select a target mailbox' });
      return;
    }

    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_ORIGIN}/api/admin/mail/aliases`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          alias: aliasEmail,
          target: aliasForm.target.trim().toLowerCase(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to create alias');
      setMsg({ err: false, text: data.message });
      setAliasForm({ aliasLocal: '', target: '' });
      setShowAddAlias(false);
      setOpenAliasFor(aliasForm.target.trim().toLowerCase());
      await load();
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const deleteAlias = async (alias, target) => {
    if (!window.confirm(`Remove alias ${alias}?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(
        `${API_ORIGIN}/api/admin/mail/aliases/${encodeURIComponent(alias)}?target=${encodeURIComponent(target)}`,
        { method: 'DELETE', headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete alias');
      setMsg({ err: false, text: data.message });
      await load();
    } catch (e) {
      setMsg({ err: true, text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const saveLimits = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`${API_ORIGIN}/api/admin/mail/limits`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          mailboxQuota: limitsForm.mailboxQuota.trim(),
          messageSize: limitsForm.messageSize.trim(),
          applyToExisting: true,
          restartMailserver: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update limits');
      setLimits(data.limits || null);
      if (data.limits) {
        setLimitsForm({
          mailboxQuota: data.limits.mailboxQuotaLabel,
          messageSize: data.limits.messageSizeLabel,
        });
      }
      setShowLimitsEdit(false);
      setMsg({ err: false, text: data.message || 'Mail size limits updated.' });
      await load();
    } catch (err) {
      setMsg({ err: true, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const toggleAliasPopover = (email) => {
    setOpenAliasFor((prev) => (prev === email ? null : email));
  };

  return (
    <div className="mail-panel">
      <header className="mail-panel-head">
        <div>
          <h2>Email administration</h2>
          <p className="mail-lead">
            Mailboxes on <strong>{host}</strong> · domain <strong>@{domain}</strong>
          </p>
        </div>
        <div className="mail-head-stats">
          <span className="mail-stat-pill">{accounts.length} mailboxes</span>
          <span className="mail-stat-pill">{aliases.length} aliases</span>
          <span className="mail-stat-pill">
            {limits?.mailboxQuotaLabel || '25 GB'} / msg {limits?.messageSizeLabel || '35 MB'}
          </span>
        </div>
      </header>

      {msg && (
        <div className={`mail-banner ${msg.err ? 'mail-banner-err' : 'mail-banner-ok'}`}>{msg.text}</div>
      )}

      <div className="mail-info-grid mail-info-grid-3">
        <div className="mail-card mail-settings-card mail-info-card">
          <h3>Client settings</h3>
          <div className="mail-settings-grid">
            <div><span className="mail-k">IMAP</span> {host}:993 SSL</div>
            <div><span className="mail-k">SMTP</span> {host}:587 STARTTLS</div>
            <div><span className="mail-k">Login</span> full email address</div>
          </div>
        </div>
        <div className="mail-card mail-settings-card mail-info-card">
          <h3>Self-service password</h3>
          <p className="mail-hint mail-hint-tight">
            Profile → <strong>Mailbox Password</strong> or login → <strong>Reset @{domain} mailbox password</strong>
          </p>
        </div>
        <div className="mail-card mail-settings-card mail-info-card">
          <div className="mail-card-head-row">
            <h3>Size limits</h3>
            <button
              type="button"
              className="mail-btn mail-btn-xs"
              onClick={() => setShowLimitsEdit((v) => !v)}
            >
              {showLimitsEdit ? 'Close' : 'Change'}
            </button>
          </div>
          {!showLimitsEdit ? (
            <div className="mail-settings-grid">
              <div><span className="mail-k">Mailbox</span> {limits?.mailboxQuotaLabel || '25 GB'} each</div>
              <div><span className="mail-k">Message</span> {limits?.messageSizeLabel || '35 MB'} max</div>
            </div>
          ) : (
            <form className="mail-limits-form" onSubmit={saveLimits}>
              <label className="mail-field">
                <span className="mail-field-label">Mailbox quota</span>
                <input
                  type="text"
                  className="mail-text-input"
                  value={limitsForm.mailboxQuota}
                  onChange={(e) => setLimitsForm((f) => ({ ...f, mailboxQuota: e.target.value }))}
                  placeholder="25GB"
                />
              </label>
              <label className="mail-field">
                <span className="mail-field-label">Per-message max</span>
                <input
                  type="text"
                  className="mail-text-input"
                  value={limitsForm.messageSize}
                  onChange={(e) => setLimitsForm((f) => ({ ...f, messageSize: e.target.value }))}
                  placeholder="35MB"
                />
              </label>
              <small className="mail-field-hint">
                Examples: 25GB, 35MB. Applies to all existing mailboxes and restarts the mail container briefly.
              </small>
              <button type="submit" className="mail-btn mail-btn-primary mail-btn-sm" disabled={busy}>
                Save limits
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="mail-toolbar">
        <input
          type="search"
          className="mail-input mail-search"
          placeholder="Search mailboxes, users, aliases…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className="mail-btn"
          onClick={() => load().catch((e) => setMsg({ err: true, text: e.message }))}
          disabled={busy}
        >
          Refresh
        </button>
        <button
          type="button"
          className={`mail-btn ${showAddAlias ? 'mail-btn-active' : ''}`}
          onClick={() => setShowAddAlias((v) => !v)}
        >
          + Add alias
        </button>
      </div>

      {showAddAlias && (
        <div className="mail-card mail-alias-card">
          <h3>New forwarding alias</h3>
          <p className="mail-hint mail-hint-tight">
            Route mail from an extra address to an existing mailbox. The alias cannot already be a mailbox.
          </p>
          <form className="mail-alias-form" onSubmit={createAlias}>
            <MailAddressInput
              id="alias-local"
              label="Alias address"
              placeholder="sales"
              localValue={aliasForm.aliasLocal}
              onLocalChange={(v) => setAliasForm((f) => ({ ...f, aliasLocal: v }))}
              domain={domain}
            />
            <div className="mail-alias-arrow" aria-hidden="true">→</div>
            <label className="mail-field mail-address-field">
              <span className="mail-field-label">Deliver to mailbox</span>
              <select
                className="mail-select"
                value={aliasForm.target}
                onChange={(e) => setAliasForm((f) => ({ ...f, target: e.target.value }))}
              >
                <option value="">Choose mailbox…</option>
                {accounts.map((a) => (
                  <option key={a.email} value={a.email}>
                    {a.email}
                  </option>
                ))}
              </select>
            </label>
            <div className="mail-alias-form-actions">
              <button type="button" className="mail-btn" onClick={() => setShowAddAlias(false)}>
                Cancel
              </button>
              <button type="submit" className="mail-btn mail-btn-primary" disabled={busy}>
                Create alias
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mail-card">
        <h3>Add mailbox</h3>
        <form className="mail-form" onSubmit={createMailbox}>
          <div className="mail-form-row mail-form-row-address">
            <MailAddressInput
              id="mailbox-local"
              label="Mailbox address"
              placeholder="support"
              localValue={addForm.localPart}
              onLocalChange={(v) => setAddForm((f) => ({ ...f, localPart: v }))}
              domain={domain}
              hint="Becomes a full login address for IMAP/SMTP"
            />
            <label className="mail-field">
              <span className="mail-field-label">Display name</span>
              <input
                type="text"
                className="mail-text-input"
                value={addForm.displayName}
                onChange={(e) => setAddForm((f) => ({ ...f, displayName: e.target.value }))}
                placeholder="Support Team"
              />
            </label>
          </div>
          <div className="mail-form-row">
            <label className="mail-field">
              <span className="mail-field-label">Link to Omni user</span>
              <select
                className="mail-select"
                value={addForm.omniUserId}
                onChange={(e) => {
                  const omniUserId = e.target.value;
                  setAddForm((f) => ({
                    ...f,
                    omniUserId,
                    notifyEmail: omniUserId ? pickUserEmail(omniUserId) : f.notifyEmail,
                  }));
                }}
              >
                <option value="">— None —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email} (#{u.id})
                  </option>
                ))}
              </select>
            </label>
            <label className="mail-field">
              <span className="mail-field-label">Notes</span>
              <input
                type="text"
                className="mail-text-input"
                value={addForm.notes}
                onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Optional admin note"
              />
            </label>
          </div>
          {addForm.generatePassword && (
            <label className="mail-field">
              <span className="mail-field-label">Email credentials &amp; setup guide to</span>
              <input
                type="email"
                className="mail-text-input"
                placeholder="user@gmail.com"
                value={addForm.notifyEmail}
                onChange={(e) => setAddForm((f) => ({ ...f, notifyEmail: e.target.value }))}
              />
              <small className="mail-field-hint">
                Password plus PC/Mac/iOS/Android setup steps. Auto-fills when you link an Omni user.
              </small>
            </label>
          )}
          <label className="mail-check">
            <input
              type="checkbox"
              checked={addForm.generatePassword}
              onChange={(e) => setAddForm((f) => ({ ...f, generatePassword: e.target.checked }))}
            />
            Generate secure password and email it to the user
          </label>
          {!addForm.generatePassword && (
            <label className="mail-field">
              <span className="mail-field-label">Password (min 8 chars)</span>
              <input
                type="text"
                className="mail-text-input"
                value={addForm.password}
                onChange={(e) => setAddForm((f) => ({ ...f, password: e.target.value }))}
                autoComplete="new-password"
              />
            </label>
          )}
          <button type="submit" className="mail-btn mail-btn-primary" disabled={busy}>
            Create mailbox
          </button>
        </form>
      </div>

      <div className="mail-card mail-mailboxes-card">
        <div className="mail-card-head-row">
          <h3>Mailboxes</h3>
          <span className="mail-count-badge">{filteredAccounts.length}</span>
        </div>
        {!filteredAccounts.length && (
          <p className="mail-empty">No mailboxes found. Create one above or check the mail server on the VPS.</p>
        )}
        {filteredAccounts.length > 0 && (
          <div className="mail-mailbox-list">
            {filteredAccounts.map((a) => {
              const mailboxAliases = aliasesByTarget[a.email.toLowerCase()] || [];
              const aliasOpen = openAliasFor === a.email;
              return (
                <div key={a.email} className={`mail-mailbox-row ${aliasOpen ? 'mail-mailbox-row-open' : ''}`}>
                  <div className="mail-mailbox-main">
                    <div className="mail-mailbox-top">
                      <div className="mail-mailbox-id">
                        <span className="mail-mailbox-email">{a.email}</span>
                        {a.displayName && <span className="mail-mailbox-display">{a.displayName}</span>}
                      </div>
                      <div className="mail-mailbox-actions">
                        <button
                          type="button"
                          className="mail-btn mail-btn-sm"
                          onClick={() => {
                            setResetTarget(a);
                            setResetNotifyEmail(a.omniEmail || '');
                            setResetEmailCredentials(true);
                            setResetGenerate(true);
                          }}
                          disabled={busy}
                        >
                          Reset PW
                        </button>
                        <button
                          type="button"
                          className="mail-btn mail-btn-sm"
                          onClick={() =>
                            setEditTarget({
                              email: a.email,
                              displayName: a.displayName || '',
                              omniUserId: a.omniUserId ? String(a.omniUserId) : '',
                              notes: a.notes || '',
                            })
                          }
                          disabled={busy}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="mail-btn mail-btn-sm mail-btn-danger"
                          onClick={() => deleteMailbox(a.email)}
                          disabled={busy}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="mail-mailbox-meta">
                      {a.omniEmail ? (
                        <span className="mail-meta-chip" title="Linked Omni user">
                          👤 {a.omniEmail}
                        </span>
                      ) : (
                        <span className="mail-meta-chip mail-meta-muted">No Omni link</span>
                      )}
                      {a.notes && (
                        <span className="mail-meta-chip mail-meta-note" title={a.notes}>
                          📝 {a.notes}
                        </span>
                      )}
                      <button
                        type="button"
                        className={`mail-alias-badge ${mailboxAliases.length ? 'mail-alias-badge-has' : 'mail-alias-badge-none'}`}
                        onClick={() => toggleAliasPopover(a.email)}
                        title={mailboxAliases.length ? 'View forwarding aliases' : 'No aliases — click to add'}
                      >
                        ↪ {mailboxAliases.length} alias{mailboxAliases.length === 1 ? '' : 'es'}
                      </button>
                    </div>
                  </div>
                  {aliasOpen && (
                    <div className="mail-mailbox-alias-panel">
                      {mailboxAliases.length > 0 ? (
                        <AliasPopover
                          aliases={mailboxAliases}
                          onDelete={deleteAlias}
                          busy={busy}
                          onClose={() => setOpenAliasFor(null)}
                        />
                      ) : (
                        <div className="mail-alias-empty-panel">
                          <p>No aliases forward to this mailbox yet.</p>
                          <button
                            type="button"
                            className="mail-btn mail-btn-sm mail-btn-primary"
                            onClick={() => {
                              setAliasForm({ aliasLocal: '', target: a.email });
                              setShowAddAlias(true);
                            }}
                          >
                            + Add alias for {a.email}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {resetTarget && (
        <div className="mail-modal-backdrop" onClick={() => !busy && setResetTarget(null)}>
          <div className="mail-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Reset password — {resetTarget.email}</h3>
            <form onSubmit={resetMailboxPassword}>
              <label className="mail-check">
                <input
                  type="checkbox"
                  checked={resetGenerate}
                  onChange={(e) => setResetGenerate(e.target.checked)}
                />
                Generate new password automatically
              </label>
              {!resetGenerate && (
                <label className="mail-field">
                  <span className="mail-field-label">New password</span>
                  <input
                    type="text"
                    className="mail-text-input"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
              )}
              {resetGenerate && (
                <>
                  <label className="mail-field">
                    <span className="mail-field-label">Email new password &amp; setup guide to</span>
                    <input
                      type="email"
                      className="mail-text-input"
                      value={resetNotifyEmail}
                      onChange={(e) => setResetNotifyEmail(e.target.value)}
                      placeholder="Linked Omni user email"
                    />
                  </label>
                  <label className="mail-check">
                    <input
                      type="checkbox"
                      checked={resetEmailCredentials}
                      onChange={(e) => setResetEmailCredentials(e.target.checked)}
                    />
                    Email credentials to user
                  </label>
                </>
              )}
              <div className="mail-modal-actions">
                <button type="button" className="mail-btn" onClick={() => setResetTarget(null)} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="mail-btn mail-btn-primary" disabled={busy}>
                  Update password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editTarget && (
        <div className="mail-modal-backdrop" onClick={() => !busy && setEditTarget(null)}>
          <div className="mail-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit — {editTarget.email}</h3>
            <form onSubmit={saveMeta}>
              <label className="mail-field">
                <span className="mail-field-label">Display name</span>
                <input
                  type="text"
                  className="mail-text-input"
                  value={editTarget.displayName}
                  onChange={(e) => setEditTarget((t) => ({ ...t, displayName: e.target.value }))}
                />
              </label>
              <label className="mail-field">
                <span className="mail-field-label">Link to Omni user</span>
                <select
                  className="mail-select"
                  value={editTarget.omniUserId}
                  onChange={(e) => setEditTarget((t) => ({ ...t, omniUserId: e.target.value }))}
                >
                  <option value="">— None —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.email} (#{u.id})
                    </option>
                  ))}
                </select>
              </label>
              <label className="mail-field">
                <span className="mail-field-label">Notes</span>
                <input
                  type="text"
                  className="mail-text-input"
                  value={editTarget.notes}
                  onChange={(e) => setEditTarget((t) => ({ ...t, notes: e.target.value }))}
                />
              </label>
              <div className="mail-modal-actions">
                <button type="button" className="mail-btn" onClick={() => setEditTarget(null)} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="mail-btn mail-btn-primary" disabled={busy}>
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
